/**
 * Graceful exit on a `decommission` member directive.
 *
 * When the product decommissions a paired subscriber (Sift's settings
 * page), it revokes the Keep credential and deactivates the Jobs pool
 * membership — but a RUNNING binary holds a cached agent token for up
 * to an hour and would keep polling into 401s. The decommission
 * directive rides the next discovery poll instead: the loop drains
 * in-flight work and the process exits with a clear message.
 *
 * No re-arm risk: a decommissioned agent's next token exchange fails at
 * Keep, so a restarted binary (or a supervisor loop) dies at boot with
 * the pairing-revoked error rather than resurrecting the subscription.
 */

import type { SubscribeLogger } from '@whittlelabs/jobs';

export function readDecommissionDirective(
  directives: Record<string, unknown>,
): { decommissioned_at?: string } | null {
  const raw = directives.decommission;
  if (!raw || typeof raw !== 'object') return null;
  return raw as { decommissioned_at?: string };
}

export interface DecommissionHooks {
  /** Drain the claim loop (waits for in-flight work). */
  stopLoop: () => Promise<void>;
  /** Test seam; defaults to `process.exit(0)` — a deliberate operator action. */
  exit?: (code: number) => void;
}

export async function shutdownOnDecommission(opts: {
  productTitle: string;
  directive: { decommissioned_at?: string };
  logger: SubscribeLogger;
  hooks: DecommissionHooks;
}): Promise<void> {
  const { productTitle, directive, logger, hooks } = opts;
  logger.info(
    `${productTitle} was decommissioned from the product settings page; draining and exiting.`,
    directive.decommissioned_at ? { decommissionedAt: directive.decommissioned_at } : undefined,
  );
  await hooks.stopLoop();
  (hooks.exit ?? ((code: number) => process.exit(code)))(0);
}
